import { ChevronDown, SearchLg } from "@untitledui/icons";
import type { FC, ReactNode } from "react";
import { isValidElement, useContext, useMemo, useRef, useState } from "react";
import {
	Button as AriaButton,
	DialogTrigger as AriaDialogTrigger,
	ListBox as AriaListBox,
	OverlayTriggerStateContext,
} from "react-aria-components";
import { Avatar } from "@/components/base/avatar/avatar";
import { Label } from "@/components/base/input/label";
import { Popover } from "@/components/base/select/popover";
import type { SelectItemType } from "@/components/base/select/select";
import { SelectContext } from "@/components/base/select/select";
import { sizes } from "@/components/base/select/select.types";
import { cx } from "@/utils/cx";
import { isReactComponent } from "@/utils/is-react-component";

export interface SearchableSelectByIdFieldProps {
	/** Form field state and handlers (value is the selected item id) */
	field: {
		state: { value: string | null; meta: { errors?: string[] } };
		handleChange: (v: string | null) => void;
		handleBlur: () => void;
	};
	/** Options to display (id, label, and optional icon) */
	items: SelectItemType[];
	/** Label above the field */
	label: string;
	/** Placeholder when nothing is selected */
	placeholder: string;
	/** Placeholder for the search input inside the dropdown (e.g. "Search users...") */
	searchPlaceholder?: string;
	/** Icon shown on the trigger when no selection (e.g. User01) */
	placeholderIcon?: FC | ReactNode;
	/** Disable the field */
	isDisabled?: boolean;
	/** Render function for each list item */
	children: (item: SelectItemType) => ReactNode;
}

/**
 * Searchable single-select that stores the selected item's id in the field.
 * Trigger shows "Select user..." or selected name with icon and chevron;
 * dropdown contains a search bar ("Search users...") then the list (mockup).
 */
export function SearchableSelectByIdField({
	field,
	items,
	label,
	placeholder,
	searchPlaceholder = "Search users...",
	placeholderIcon,
	isDisabled,
	children,
}: SearchableSelectByIdFieldProps) {
	const triggerRef = useRef<HTMLButtonElement>(null);
	const [searchQuery, setSearchQuery] = useState("");
	const selectedId = field.state.value;
	const selectedItem = items.find((i) => i.id === selectedId);
	const filteredItems = useMemo(() => {
		const q = searchQuery.trim().toLowerCase();
		const filtered = q
			? items.filter((i) => (i.label ?? "").toLowerCase().includes(q))
			: items;
		if (selectedItem && !filtered.some((i) => i.id === selectedId)) {
			return [selectedItem, ...filtered];
		}
		return filtered;
	}, [items, searchQuery, selectedId, selectedItem]);

	return (
		<div className="space-y-2">
			{label && (
				<Label className="block text-sm font-medium text-primary">
					{label}
				</Label>
			)}
			<SelectContext.Provider value={{ size: "sm" }}>
				<AriaDialogTrigger>
					<AriaButton
						ref={triggerRef}
						isDisabled={isDisabled}
						onBlur={field.handleBlur}
						className={({ isFocusVisible }) =>
							cx(
								"relative flex w-full cursor-pointer items-center rounded-lg bg-primary shadow-xs ring-1 ring-primary outline-hidden transition duration-100 ease-linear ring-inset",
								isFocusVisible && "ring-2 ring-brand",
								isDisabled &&
									"cursor-not-allowed bg-disabled_subtle text-disabled",
								sizes.sm.root,
							)
						}
					>
						{selectedItem?.avatarUrl ? (
							<Avatar
								size="xs"
								src={selectedItem.avatarUrl}
								alt={selectedItem.label}
							/>
						) : (
							(() => {
								const Icon = selectedItem?.icon ?? placeholderIcon;
								return isReactComponent(Icon) ? (
									<Icon
										data-icon
										aria-hidden="true"
										className="size-5 shrink-0 text-fg-quaternary"
									/>
								) : isValidElement(Icon) ? (
									Icon
								) : null;
							})()
						)}
						<span
							className={cx(
								"flex flex-1 truncate text-left text-md",
								selectedItem ? "font-medium text-primary" : "text-placeholder",
								isDisabled && "text-disabled",
							)}
						>
							{selectedItem?.label ?? placeholder}
						</span>
						<ChevronDown
							aria-hidden="true"
							className="ml-auto shrink-0 text-fg-quaternary size-4 stroke-[2.5px]"
						/>
					</AriaButton>

					<Popover
						triggerRef={triggerRef}
						size="sm"
						className="overflow-hidden"
					>
						<div className="flex max-h-64 flex-col overflow-hidden">
							<div className="shrink-0 border-b border-secondary_alt bg-primary p-2">
								<div className="flex items-center gap-2 rounded-md ring-1 ring-primary ring-inset">
									<SearchLg className="size-5 shrink-0 text-fg-quaternary ml-2" />
									<input
										type="text"
										placeholder={searchPlaceholder}
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
										onKeyDown={(e) => e.stopPropagation()}
										className="w-full appearance-none bg-transparent py-2 pr-2 text-md text-primary placeholder:text-placeholder focus:outline-none"
										aria-label={searchPlaceholder}
									/>
								</div>
							</div>
							<div className="min-h-0 flex-1 overflow-y-auto">
								<SearchableSelectList
									items={filteredItems}
									selectedId={selectedId}
									onSelect={(id) => {
										field.handleChange(id);
										setSearchQuery("");
									}}
									renderItem={children}
								/>
							</div>
						</div>
					</Popover>
				</AriaDialogTrigger>
			</SelectContext.Provider>
			{field.state.meta.errors?.length ? (
				<p className="text-sm text-error-600">{field.state.meta.errors[0]}</p>
			) : null}
		</div>
	);
}

function SearchableSelectList({
	items,
	selectedId,
	onSelect,
	renderItem,
}: {
	items: SelectItemType[];
	selectedId: string | null;
	onSelect: (id: string | null) => void;
	renderItem: (item: SelectItemType) => ReactNode;
}) {
	const overlayState = useContext(OverlayTriggerStateContext);

	return (
		<AriaListBox
			items={items}
			selectedKeys={selectedId ? [selectedId] : []}
			selectionMode="single"
			className="size-full outline-hidden"
			onAction={(key) => {
				onSelect(key != null ? String(key) : null);
				overlayState?.close();
			}}
		>
			{(item) => renderItem(item)}
		</AriaListBox>
	);
}
